import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import useFetch from 'use-http';
import { X, FileText, Loader2 } from 'src/components/common/icons';
import { ActionButton } from './Header';
import TinHistoryModal from './TinHistoryModal';
import ScanByNameModal from './ScanByNameModal';
import ScanByTinModal from './ScanByTinModal';
import BNoticeMaintenanceDialog from './BNoticeMaintenanceDialog';
import Company1099MaintenanceDialog from './Company1099MaintenanceDialog';
import TinMatchingCsvDialog from './TinMatchingCsvDialog';
import LeaseGl1099ReportDialog from './LeaseGl1099ReportDialog';

// --- Type Definitions ---
export interface ICompany {
    id: number;
    name: string;
}

export interface ILeaseTinDetail {
    lsNo: number;
    chkDate: string | null;
    amount: number | null;
    clNo: number | null;
    clName: string | null;
    name1: string | null;
    transmitDate: string | null;
    correctDate: string | null;
    correctAmt: number | null;
    timestamp: string | null;
}

export interface ILeaseTin {
    tinId: number;
    tin: string | null;
    tinType: string | null;
    taxYear: number | null;
    compId: number | null;
    amount: number | null;
    name1: string | null;
    addr1: string | null;
    cstz: string | null;
    modBy: string | null;
    modDate: string | null;
    transmitDate: string | null;
    correctAmount: number | null;
    correctDate: string | null;
    correctTransDate: string | null;
    mailDate: string | null;
    withAmount: number | null;
    tinVerified: number | null;
    tinVerDate: string | null;
    leasesCount?: number | null;
    details: ILeaseTinDetail[];
}

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    // Made lsNo optional, added tinId
    lsNo?: number;
    tinId?: number;
}

const Maintenance1099Modal: React.FC<IProps> = ({ isOpen, onClose, lsNo, tinId }) => {
    const { get, response, loading } = useFetch();

    const [data, setData] = useState<ILeaseTin | null>(null);
    const [companies, setCompanies] = useState<ICompany[]>([]);

    // Find Dialogs
    const [isFindByNameOpen, setIsFindByNameOpen] = useState(false);
    const [findNameInput, setFindNameInput] = useState('');
    const [findTaxYearInput, setFindTaxYearInput] = useState('');

    const [isFindByTinOpen, setIsFindByTinOpen] = useState(false);
    const [findTinInput, setFindTinInput] = useState('');
    const [findTinTaxYearInput, setFindTinTaxYearInput] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Scan & History Dialog Triggers
    const [isScanByNameOpen, setIsScanByNameOpen] = useState(false);
    const [isScanByTinOpen, setIsScanByTinOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [isBNoticeOpen, setIsBNoticeOpen] = useState(false);
    const [isCompanyMaintenanceOpen, setIsCompanyMaintenanceOpen] = useState(false);
    const [isTinMatchingOpen, setIsTinMatchingOpen] = useState(false);
    const [isLeaseGl1099Open, setIsLeaseGl1099Open] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // Load based on which identifier was provided
            if (tinId) {
                loadDataByTinId(tinId);
            } else if (lsNo) {
                loadDataByLease(lsNo);
            }
            loadCompanies();
        } else {
            setData(null);
            setIsFindByNameOpen(false);
            setIsFindByTinOpen(false);
            setIsScanByNameOpen(false);
            setIsScanByTinOpen(false);
            setIsHistoryModalOpen(false);
            setIsBNoticeOpen(false);
            setIsCompanyMaintenanceOpen(false);
            setIsTinMatchingOpen(false);
            setIsLeaseGl1099Open(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, lsNo, tinId]);

    // NEW: Load directly by tinId
    const loadDataByTinId = async (id: number) => {
        const result = await get(`/v1.0/leases/tins/${id}`);
        if (response.ok && result) {
            setData(result as ILeaseTin);
        }
    };

    // EXISTING: Load by lease number
    const loadDataByLease = async (leaseNo: number) => {
        const result = await get(`/v1.0/leases/tins/by-lease/${leaseNo}`);
        if (response.ok && result) {
            setData(result as ILeaseTin);
        }
    };

    const loadCompanies = async () => {
        const comps = await get('/v1.0/leases/master/companies');
        if (response.ok && Array.isArray(comps)) {
            setCompanies(comps as ICompany[]);
        }
    };

    // Callback used by both Scan Modals to load a selected record into the main view
    const handleSelectScanResult = async (selectedTinId: number) => {
        loadDataByTinId(selectedTinId);
        setIsScanByNameOpen(false);
        setIsScanByTinOpen(false);
    };

    const handleFindByNameSubmit = async () => {
        if (!findNameInput.trim()) {
            return alert('TIN Name is required.');
        }
        setIsSearching(true);
        let url = `/v1.0/leases/tins/find-by-name?name=${encodeURIComponent(findNameInput)}`;
        if (findTaxYearInput.trim()) {
            url += `&taxYear=${encodeURIComponent(findTaxYearInput)}`;
        }

        const result = await get(url);
        setIsSearching(false);
        if (response.ok && result) {
            setData(result as ILeaseTin);
            setIsFindByNameOpen(false);
            setFindNameInput('');
            setFindTaxYearInput('');
        } else {
            alert('No TIN record found matching those criteria.');
        }
    };

    const handleFindByTinSubmit = async () => {
        if (!findTinInput.trim()) {
            return alert('TIN is required.');
        }
        setIsSearching(true);
        let url = `/v1.0/leases/tins/find-by-tin?tin=${encodeURIComponent(findTinInput)}`;
        if (findTinTaxYearInput.trim()) {
            url += `&taxYear=${encodeURIComponent(findTinTaxYearInput)}`;
        }

        const result = await get(url);
        setIsSearching(false);
        if (response.ok && result) {
            setData(result as ILeaseTin);
            setIsFindByTinOpen(false);
            setFindTinInput('');
            setFindTinTaxYearInput('');
        } else {
            alert('No TIN record found matching those criteria.');
        }
    };

    const handleOpenHistory = async () => {
        if (!data || !data.tinId) {
            return;
        }
        const checkRes = await get(`/v1.0/leases/tins/${data.tinId}/history-check`);
        if (response.ok && checkRes?.hasHistory) {
            setIsHistoryModalOpen(true);
        } else {
            alert('No history records exist for this TIN.');
        }
    };

    const formatMoney = (val?: number | null) =>
        val !== null && val !== undefined ? val.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '';
    const formatDate = (val?: string | null) => (val ? new Date(val).toLocaleDateString() : '');

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={{ ...styles.modalContent, width: '1250px', maxWidth: '95vw' }} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>
                        <FileText style={styles.headerIcon} />
                        1099 Maintenance - Version 2.0
                    </h2>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                <div
                    style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem', backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}
                >
                    <ActionButton label="Find by Name" onClick={() => setIsFindByNameOpen(true)} />
                    <ActionButton label="Find by TIN" onClick={() => setIsFindByTinOpen(true)} />
                    <ActionButton label="Scan by Name" onClick={() => setIsScanByNameOpen(true)} />
                    <ActionButton label="Scan by TIN" onClick={() => setIsScanByTinOpen(true)} />
                    <ActionButton label="TIN History" onClick={handleOpenHistory} />
                    <ActionButton label="B-Notice Maintenance" onClick={() => setIsBNoticeOpen(true)} />
                    <ActionButton label="1099 Company Maintenance" onClick={() => setIsCompanyMaintenanceOpen(true)} />
                    <ActionButton label="TIN Matching CSV" onClick={() => setIsTinMatchingOpen(true)} active={true} />
                    <ActionButton label="Lease 1099 / GL Reconciliation" onClick={() => setIsLeaseGl1099Open(true)} />
                </div>

                <div style={styles.modalBody}>
                    {!data && loading ? (
                        <div style={styles.loadingState}>
                            <Loader2 className="w-8 h-8 animate-spin" style={styles.loaderIcon} />
                            <span style={styles.loadingText}>Fetching TIN Data...</span>
                        </div>
                    ) : !data ? (
                        <div style={styles.emptyState}>
                            <FileText style={styles.emptyIcon} />
                            <span style={styles.emptyText}>No 1099/TIN data found. Try searching.</span>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '2rem' }}>
                            {/* Top Form Section */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '3rem' }}>
                                {/* Left Form */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '120px 150px 70px 100px 50px 100px',
                                            gap: '0.5rem',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <span style={localStyles.label}>Tax ID No.</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, color: '#4F46E5', fontWeight: 'bold' }}
                                            readOnly={true}
                                            value={data.tin || ''}
                                        />
                                        <span style={{ ...localStyles.label, textAlign: 'right' }}>Tax Year</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={data.taxYear || ''} />
                                        <span style={{ ...localStyles.label, textAlign: 'right' }}>Type</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={data.tinType || ''} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Company</span>
                                        <select style={localStyles.input} disabled={true} value={data.compId?.toString() || ''}>
                                            <option value="">Select...</option>
                                            {companies.map((c) => (
                                                <option key={c.id} value={c.id.toString()}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Name</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={data.name1 || ''} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Address</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={data.addr1 || ''} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>City, State Zip</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={data.cstz || ''} />
                                    </div>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '120px 100px 150px 200px',
                                            gap: '0.5rem',
                                            alignItems: 'center',
                                            marginTop: '0.5rem',
                                        }}
                                    >
                                        <span style={localStyles.label}>Verified</span>
                                        <div style={localStyles.greenBadge}>{data.tinVerified ? 'YES' : 'NO'}</div>
                                        <div style={localStyles.greenBadge}>PREVIOUS YEAR</div>
                                        <div style={localStyles.greenText}>{formatDate(data.tinVerDate)}</div>
                                    </div>
                                </div>

                                {/* Right Form */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Tax ID</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, textAlign: 'center' }}
                                            readOnly={true}
                                            value={data.tinId || ''}
                                        />
                                    </div>
                                    <div style={{ height: '0.5rem' }} />
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Total Amount</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, textAlign: 'right' }}
                                            readOnly={true}
                                            value={formatMoney(data.amount)}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Total With. Amount</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, textAlign: 'right' }}
                                            readOnly={true}
                                            value={formatMoney(data.withAmount)}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Mail Date</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={formatDate(data.mailDate)} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Transmit Date</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={formatDate(data.transmitDate)} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Correction Amt.</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, textAlign: 'right' }}
                                            readOnly={true}
                                            value={formatMoney(data.correctAmount)}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Correction Date</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={formatDate(data.correctDate)} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Corr. Trans Date</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={formatDate(data.correctTransDate)} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Modified By</span>
                                        <LeaseRecordInput
                                            style={{ ...localStyles.input, textTransform: 'uppercase' }}
                                            readOnly={true}
                                            value={data.modBy || ''}
                                        />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem', alignItems: 'center' }}>
                                        <span style={localStyles.label}>Modified at</span>
                                        <LeaseRecordInput style={localStyles.input} readOnly={true} value={formatDate(data.modDate)} />
                                    </div>
                                </div>
                            </div>

                            {/* Details Grid Section */}
                            <div style={styles.tableWrapper}>
                                <table style={styles.tableBase}>
                                    <thead style={styles.tableHead}>
                                        <tr>
                                            <th style={styles.tableHeaderCell}>Lease No.</th>
                                            <th style={styles.tableHeaderCell}>Check Date</th>
                                            <th style={{ ...styles.tableHeaderCell, textAlign: 'right' }}>Amount</th>
                                            <th style={{ ...styles.tableHeaderCell, textAlign: 'right' }}>Cl. No.</th>
                                            <th style={styles.tableHeaderCell}>Client Name</th>
                                            <th style={styles.tableHeaderCell}>Trans. Date</th>
                                            <th style={{ ...styles.tableHeaderCell, textAlign: 'right' }}>Corr. Amount</th>
                                            <th style={styles.tableHeaderCell}>Corr. Date</th>
                                            <th style={styles.tableHeaderCell}>Timestamp</th>
                                        </tr>
                                    </thead>
                                    <tbody style={styles.tableBody}>
                                        {!data.details || data.details.length === 0 ? (
                                            <tr>
                                                <td colSpan={9} style={styles.noResults}>
                                                    No details available
                                                </td>
                                            </tr>
                                        ) : (
                                            data.details.map((row: ILeaseTinDetail, i: number) => (
                                                <tr key={i} style={styles.tableRow}>
                                                    <td style={styles.tableDataCell}>
                                                        <button
                                                            style={styles.checkNoButton}
                                                            onDoubleClick={() => window.open(`/leases/${row.lsNo}`, '_blank')}
                                                            title="Double click to open Lease in new tab"
                                                        >
                                                            {row.lsNo}
                                                        </button>
                                                    </td>
                                                    <td style={styles.tableDataCell}>{formatDate(row.chkDate)}</td>
                                                    <td style={{ ...styles.tableDataCell, ...styles.amountCell, textAlign: 'right' }}>
                                                        {formatMoney(row.amount)}
                                                    </td>
                                                    <td style={{ ...styles.tableDataCell, textAlign: 'right' }}>{row.clNo}</td>
                                                    <td style={styles.tableDataCell}>{row.clName}</td>
                                                    <td style={styles.tableDataCell}>{formatDate(row.transmitDate)}</td>
                                                    <td style={{ ...styles.tableDataCell, ...styles.amountCell, textAlign: 'right' }}>
                                                        {formatMoney(row.correctAmt)}
                                                    </td>
                                                    <td style={styles.tableDataCell}>{formatDate(row.correctDate)}</td>
                                                    <td style={styles.tableDataCell}>{formatDate(row.timestamp)}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <div style={styles.modalFooter}>
                    <button onClick={onClose} style={styles.buttonPrimary}>
                        Close
                    </button>
                </div>
            </div>

            {/* --- FIND BY NAME SUB-DIALOG --- */}
            {isFindByNameOpen && (
                <div
                    style={styles.subModalOverlay}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsFindByNameOpen(false);
                    }}
                >
                    <div style={styles.subModalContent} onClick={(e) => e.stopPropagation()}>
                        <div
                            style={{
                                padding: '1rem 1.5rem',
                                borderBottom: '1px solid #E5E7EB',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: '#F9FAFB',
                            }}
                        >
                            <h3 style={{ margin: 0, fontSize: '1rem', color: '#111827' }}>Find By Name</h3>
                            <button onClick={() => setIsFindByNameOpen(false)} style={styles.closeButton}>
                                <X style={{ width: '1rem', height: '1rem' }} />
                            </button>
                        </div>
                        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '1rem', alignItems: 'center' }}>
                                <label style={localStyles.label}>TIN Name</label>
                                <LeaseRecordInput
                                    style={{ ...localStyles.input, backgroundColor: '#FFFFFF' }}
                                    value={findNameInput}
                                    onChange={(e) => setFindNameInput(e.target.value)}
                                    placeholder="e.g. Jones"
                                    autoFocus={true}
                                    onKeyDown={(e) => e.key === 'Enter' && handleFindByNameSubmit()}
                                />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '1rem', alignItems: 'center' }}>
                                <label style={localStyles.label}>Tax Year</label>
                                <LeaseRecordInput
                                    style={{ ...localStyles.input, backgroundColor: '#FFFFFF' }}
                                    type="number"
                                    value={findTaxYearInput}
                                    onChange={(e) => setFindTaxYearInput(e.target.value)}
                                    placeholder="Optional"
                                    onKeyDown={(e) => e.key === 'Enter' && handleFindByNameSubmit()}
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                padding: '1rem 1.5rem',
                                borderTop: '1px solid #E5E7EB',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '0.75rem',
                            }}
                        >
                            <button
                                onClick={() => setIsFindByNameOpen(false)}
                                style={{ ...styles.buttonPrimary, backgroundColor: '#FFFFFF', color: '#374151', border: '1px solid #D1D5DB' }}
                            >
                                Cancel
                            </button>
                            <button onClick={handleFindByNameSubmit} disabled={isSearching} style={styles.buttonPrimary}>
                                {isSearching ? 'Searching...' : 'OK'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- FIND BY TIN SUB-DIALOG --- */}
            {isFindByTinOpen && (
                <div
                    style={styles.subModalOverlay}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsFindByTinOpen(false);
                    }}
                >
                    <div style={styles.subModalContent} onClick={(e) => e.stopPropagation()}>
                        <div
                            style={{
                                padding: '1rem 1.5rem',
                                borderBottom: '1px solid #E5E7EB',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: '#F9FAFB',
                            }}
                        >
                            <h3 style={{ margin: 0, fontSize: '1rem', color: '#111827' }}>Find By TIN</h3>
                            <button onClick={() => setIsFindByTinOpen(false)} style={styles.closeButton}>
                                <X style={{ width: '1rem', height: '1rem' }} />
                            </button>
                        </div>
                        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '1rem', alignItems: 'center' }}>
                                <label style={localStyles.label}>TIN No.</label>
                                <LeaseRecordInput
                                    style={{ ...localStyles.input, backgroundColor: '#FFFFFF' }}
                                    value={findTinInput}
                                    onChange={(e) => setFindTinInput(e.target.value)}
                                    placeholder="e.g. 123456789"
                                    autoFocus={true}
                                    onKeyDown={(e) => e.key === 'Enter' && handleFindByTinSubmit()}
                                />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '1rem', alignItems: 'center' }}>
                                <label style={localStyles.label}>Tax Year</label>
                                <LeaseRecordInput
                                    style={{ ...localStyles.input, backgroundColor: '#FFFFFF' }}
                                    type="number"
                                    value={findTinTaxYearInput}
                                    onChange={(e) => setFindTinTaxYearInput(e.target.value)}
                                    placeholder="Optional"
                                    onKeyDown={(e) => e.key === 'Enter' && handleFindByTinSubmit()}
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                padding: '1rem 1.5rem',
                                borderTop: '1px solid #E5E7EB',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '0.75rem',
                            }}
                        >
                            <button
                                onClick={() => setIsFindByTinOpen(false)}
                                style={{ ...styles.buttonPrimary, backgroundColor: '#FFFFFF', color: '#374151', border: '1px solid #D1D5DB' }}
                            >
                                Cancel
                            </button>
                            <button onClick={handleFindByTinSubmit} disabled={isSearching} style={styles.buttonPrimary}>
                                {isSearching ? 'Searching...' : 'OK'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- REFACTORED SCAN BY NAME SUB-DIALOG --- */}
            <ScanByNameModal isOpen={isScanByNameOpen} onClose={() => setIsScanByNameOpen(false)} onSelect={handleSelectScanResult} />

            {/* --- NEW SCAN BY TIN SUB-DIALOG --- */}
            <ScanByTinModal isOpen={isScanByTinOpen} onClose={() => setIsScanByTinOpen(false)} onSelect={handleSelectScanResult} />

            {/* --- TIN HISTORY SUB-DIALOG --- */}
            <TinHistoryModal isOpen={isHistoryModalOpen} onClose={() => setIsHistoryModalOpen(false)} tinId={data?.tinId ?? null} />

            <BNoticeMaintenanceDialog isOpen={isBNoticeOpen} onClose={() => setIsBNoticeOpen(false)} />

            <Company1099MaintenanceDialog isOpen={isCompanyMaintenanceOpen} onClose={() => setIsCompanyMaintenanceOpen(false)} />

            <TinMatchingCsvDialog isOpen={isTinMatchingOpen} onClose={() => setIsTinMatchingOpen(false)} />

            <LeaseGl1099ReportDialog isOpen={isLeaseGl1099Open} onClose={() => setIsLeaseGl1099Open(false)} />
        </div>
    );
};

// --- Standard Styles ---
const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '90%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: {
        fontSize: '1.5rem',
        fontWeight: 700,
        color: '#111827',
        display: 'flex',
        alignItems: 'center',
        margin: 0,
    },
    headerIcon: {
        width: '1.5rem',
        height: '1.5rem',
        marginRight: '0.5rem',
        color: '#4F46E5',
    },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },
    modalBody: {
        padding: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflow: 'hidden',
    },
    modalFooter: {
        padding: '1rem',
        borderTop: '1px solid #E5E7EB',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
    },
    buttonPrimary: {
        padding: '0.6rem 1.5rem',
        border: 'none',
        borderRadius: '0.5rem',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.15s ease-in-out',
    },
    loadingState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '12.5rem',
        color: '#4F46E5',
    },
    loaderIcon: { width: '2rem', height: '2rem', marginRight: '0.5rem', animation: 'spin 1s linear infinite' },
    loadingText: { fontSize: '1.125rem', fontWeight: 500 },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '12.5rem',
        color: '#6B7280',
    },
    emptyIcon: { width: '2.5rem', height: '2.5rem', marginBottom: '0.5rem' },
    emptyText: { fontSize: '1.125rem', fontWeight: 500 },
    tableWrapper: {
        overflowY: 'auto',
        flexGrow: 1,
        borderRadius: '0.5rem',
        border: '1px solid #E5E7EB',
        backgroundColor: 'white',
    },
    tableBase: { minWidth: '100%', borderCollapse: 'collapse' },
    tableHead: { backgroundColor: '#F9FAFB', position: 'sticky', top: 0, zIndex: 1 },
    tableHeaderCell: {
        padding: '0.75rem 1rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #E5E7EB',
    },
    tableBody: { backgroundColor: '#FFFFFF' },
    tableRow: { transition: 'background-color 0.1s ease', borderBottom: '1px solid #F3F4F6' },
    tableDataCell: { padding: '0.75rem 1rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#111827' },
    checkNoButton: {
        fontWeight: 600,
        color: '#4F46E5',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        textDecoration: 'underline',
        padding: 0,
        fontSize: '0.875rem',
    },
    amountCell: { fontWeight: 600 },
    noResults: { padding: '1.5rem', textAlign: 'center', color: '#6B7280', fontSize: '0.875rem' },
    subModalOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 60,
        backdropFilter: 'blur(2px)',
        borderRadius: '1rem',
    },
    subModalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '0.5rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        width: '400px',
        border: '1px solid #E5E7EB',
        display: 'flex',
        flexDirection: 'column',
    },
};

// Form specific & Creative row styles
const localStyles: { [key: string]: React.CSSProperties } = {
    label: { fontSize: '0.875rem', color: '#374151', fontWeight: 500, whiteSpace: 'nowrap' },
    input: {
        padding: '0.5rem 0.75rem',
        border: '1px solid #D1D5DB',
        borderRadius: '0.375rem',
        fontSize: '0.875rem',
        color: '#111827',
        backgroundColor: '#F9FAFB',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
    },
    greenBadge: {
        backgroundColor: '#10B981',
        color: 'white',
        padding: '0.25rem 0.5rem',
        borderRadius: '0.25rem',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    greenText: { color: '#10B981', fontSize: '0.875rem', fontWeight: 'bold' },
};

export default Maintenance1099Modal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/Maintenance1099Modal.tsx